<template>
    <div class="">
        <!-- DataTable Component -->
        <DataTable
            :perPage="itemsPerPage"
            :key="tableKey"
            :columns="visibleColumns"
            :items="paginatedItems"
            :currentPage="currentPage"
            :totalPages="totalPages"
            :enableDragDrop="false"
            :enableMultiSelect="false"
            rowClass="text-center"
            :sortBy="orderBy"
            :sortOrder="orderDirection"
            @page-changed="handlePageChange"
            @sort="handleSort"
        >
            <!-- Toolbar Slot -->
            <template #toolbar>
                <div class="ppv2-flex ppv2-flex-wrap ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                    <!-- Search Input -->
                    <div class="ppv2-relative">
                        <Search v-model="searchQuery" :emitGlobal="false" @search-updated="handleSearchUpdate" />
                    </div>

                    <!-- Status Filter -->
                    <FilterByType
                        :label="$t('voucher.all_status')"
                        :options="statusOptions"
                        @filter-selected="handleStatusChange"
                    />

                    <div class="ppv2-flex-1"></div>

                    <!-- Create Button -->
                    <BaseButton
                        variant="purple"
                        :custom-height="40"
                        :custom-font-size="14"
                        custom-class="ppv2-gap-3 ppv2-pl-4 ppv2-pr-[20px]"
                        @click="navigateToCreate"
                    >
                        <Plus :size="17" />
                        <span class="poppinsRegular">{{ $t('promotions.btn_add') }}</span>
                    </BaseButton>
                </div>
            </template>

            <!-- Column: Title -->
            <template #column-title="{ item }">
                <a
                    :href="`/back-office/v3/promotions/${item.id}/edit`"
                    data-turbo="false"
                    class="ppv2-text-black poppinsRegular hover:ppv2-text-[#7D2DBD] ppv2-transition-colors ppv2-underline"
                    :style="{ fontSize: normalizeFontSize(14) }"
                >
                    {{ item.title }}
                </a>
            </template>

            <!-- Column: Expiration Date -->
            <template #column-expiration_date="{ item }">
                <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.expiration_date ? moment(item.expiration_date).format('DD/MM/YYYY') : '-' }}
                </span>
            </template>

            <!-- Column: Start Date -->
            <template #column-start_date="{ item }">
                <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.start_date ? moment(item.start_date).format('DD/MM/YYYY') : '-' }}
                </span>
            </template>

            <!-- Column: Status -->
            <template #column-status="{ item }">
                <div class="ppv2-flex ppv2-justify-center">
                    <span
                        v-if="item.disabled"
                        class="ppv2-px-2 ppv2-py-1 ppv2-rounded-full ppv2-text-xs ppv2-bg-gray-100 ppv2-text-gray-800"
                    >
                        {{ $t('common.disabled') }}
                    </span>

                    <span
                        v-else-if="item.is_expired"
                        class="ppv2-px-2 ppv2-py-1 ppv2-rounded-full ppv2-text-xs ppv2-bg-red-100 ppv2-text-red-800"
                    >
                        {{ $t('common.expired') }}
                    </span>

                    <span v-else class="ppv2-px-2 ppv2-py-1 ppv2-rounded-full ppv2-text-xs ppv2-bg-green-100 ppv2-text-green-800">
                        {{ $t('common.enabled') }}
                    </span>
                </div>
            </template>

            <template #column-uses="{ item }">
                <div class="ppv2-flex ppv2-justify-center">
                    <span v-if="!item.uses" class="ppv2-px-2 ppv2-py-1 ppv2-text-xs">
                        {{ $t('promotions.unlimited_uses') }}
                    </span>
                    <span v-else class="ppv2-px-2 ppv2-py-1 ppv2-text-xs"> {{ item.uses }} </span>
                </div>
            </template>

            <template #column-used="{ item }">
                <div class="ppv2-flex ppv2-justify-center">
                    {{ item.used }}
                </div>
            </template>

            <!-- Column: Discount Amount -->
            <template #column-discount_amount="{ item }">
                <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.discount_amount }} {{ item.discount_type === 'percentage' ? '%' : $symbolCurrency }}
                </span>
            </template>

            <!-- Column: Actions -->
            <template #column-actions="{ item }">
                <div class="ppv2-relative ppv2-flex ppv2-items-center ppv2-justify-center">
                    <button
                        @click.stop="toggleActionMenu(item.id)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :ref="`actionButton-${item.id}`"
                    >
                        <MoreVertical :size="18" />
                    </button>

                    <!-- Actions Dropdown Menu -->
                    <div
                        v-if="activeActionMenu === item.id"
                        class="actionsDropdown ppv2-fixed ppv2-rounded-xl ppv2-overflow-hidden ppv2-z-[100] ppv2-min-w-[220px] ppv2-shadow-lg"
                        :style="dropdownPosition"
                        :ref="`actionMenu-${item.id}`"
                    >
                        <!-- Modifier -->
                        <a
                            :href="`/back-office/v3/promotions/${item.id}/edit`"
                            data-turbo="false"
                            class="actionItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-3 ppv2-cursor-pointer ppv2-transition-colors"
                        >
                            <FilePen :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                                {{ $t('promotions.actions.edit') }}
                            </span>
                        </a>

                        <!-- Togoogle Status -->
                        <div
                            @click="handleToggleStatus(item.id, item.disabled)"
                            class="actionItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-3 ppv2-cursor-pointer ppv2-transition-colors"
                        >
                            <component :is="item.disabled ? 'CheckCircle' : 'XCircle'" :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                                {{ item.disabled ? $t('promotions.actions.enable') : $t('promotions.actions.disable') }}
                            </span>
                        </div>

                        <!-- Supprimer -->
                        <!-- <div
                            @click="deleteItem(item.id)"
                            class="actionItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-3 ppv2-cursor-pointer ppv2-transition-colors ppv2-text-red-600"
                        >
                            <Trash2 :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                                {{ $t('promotions.actions.delete') }}
                            </span>
                        </div>-->
                    </div>
                </div>
            </template>
        </DataTable>

        <!-- Delete Confirmation Popup -->
        <CustomDialog v-model="showDeletePopup" :defaultTitle="deletePopupTitle" customClassTitle="poppinsSemiBold" noScroll>
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ deletePopupDescription }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    variant="outline"
                    @click="showDeletePopup = false"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]`"
                    :customHeight="45"
                >
                    {{ $t('common.cancel') }}
                </BaseButton>
                <BaseButton
                    variant="danger"
                    @click="confirmDelete"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]`"
                    :customHeight="45"
                >
                    {{ $t('common.confirm') }}
                </BaseButton>
            </template>
        </CustomDialog>

        <!-- Code utilisé sur des devis en attente : on ne supprime pas, on propose de désactiver
             (garder sur ces devis) ou de révoquer (l'en retirer). -->
        <CustomDialog
            v-model="showInUseDialog"
            :defaultTitle="$t('promotions.in_use.title')"
            customClassTitle="poppinsSemiBold"
            noScroll
        >
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ $t('promotions.in_use.description', { count: inUseCount }) }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    variant="outline"
                    @click="closeInUseDialog"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[150px]`"
                    :customHeight="45"
                >
                    {{ $t('common.cancel') }}
                </BaseButton>
                <BaseButton
                    variant="outline"
                    @click="disableInUse"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[150px]`"
                    :customHeight="45"
                >
                    {{ $t('promotions.in_use.disable') }}
                </BaseButton>
                <BaseButton
                    variant="danger"
                    @click="revokeInUse"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[150px]`"
                    :customHeight="45"
                >
                    {{ $t('promotions.in_use.revoke') }}
                </BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import { Trash2, Plus, FilePen, ChevronDown, MoreVertical, CheckCircle, XCircle } from 'lucide-vue'
import DataTable from '../../../Shared/DataTable.vue'
import Search from '../../Experience/components/Search.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import CustomDialog from '../../../Shared/CustomDialog.vue'
import FilterByType from '../../../Shared/FilterByType.vue'
import normalizeSize from '../../../../utils/normalizeSize'
import axios from 'axios'
import moment from 'moment'
import { eventBus } from '../../../Customer/eventBus'
import normalizeFontSize from '../../../../utils/normalizeFontSize'
import { getPerPage, setPerPage } from '../../../../utils/perPage'

export default {
    name: 'PromotionTable',
    components: {
        Trash2,
        Plus,
        FilePen,
        ChevronDown,
        MoreVertical,
        CheckCircle,
        XCircle,
        DataTable,
        Search,
        BaseButton,
        CustomDialog,
        FilterByType,
    },
    props: {
        token: {
            type: [String],
            default: '',
        },
    },
    data() {
        return {
            searchQuery: '',
            statusFilter: '',
            statusOptions: [
                { value: '', label: this.$t('voucher.all_status') },
                { value: 'active', label: this.$t('common.enabled') },
                { value: 'disabled', label: this.$t('common.disabled') },
                { value: 'expired', label: this.$t('common.expired') },
            ],
            items: {
                data: [],
                total: 0,
                last_page: 1,
            },
            itemsPerPage: getPerPage('promotions_items_per_page', 10),
            currentPage: 1,
            isLoading: false,
            error: null,
            orderBy: 'id',
            orderDirection: 'desc',
            activeActionMenu: null,
            dropdownPosition: { top: '0px', left: '0px' },
            showDeletePopup: false,
            itemToDelete: null,
            // Dialogue « code utilisé sur des devis en attente » (réponse 409 de la suppression).
            showInUseDialog: false,
            inUseCount: 0,
            selectedColumnKeys: [
                'title',
                'code',
                'discount_amount',
                'uses',
                'used',
                'start_date',
                'expiration_date',
                'status',
                'actions',
            ],
            tableKey: 0,
            deletePopupTitle: this.$t('blog.delete_title'),
            deletePopupDescription: '',
        }
    },
    computed: {
        allColumns() {
            return [
                { key: 'title', label: this.$t('promotions.datatable.title'), sortable: true },
                { key: 'code', label: this.$t('promotions.datatable.code'), sortable: true },
                { key: 'uses', label: this.$t('promotions.datatable.uses'), sortable: true },
                { key: 'used', label: this.$t('promotions.datatable.used'), sortable: true },
                { key: 'discount_amount', label: this.$t('promotions.datatable.discount_amount'), sortable: true },
                { key: 'start_date', label: this.$t('promotions.datatable.start_date'), sortable: true },
                { key: 'expiration_date', label: this.$t('promotions.datatable.expiration_date'), sortable: true },
                { key: 'status', label: this.$t('promotions.datatable.status'), sortable: false },
                { key: 'actions', label: this.$t('promotions.datatable.actions'), sortable: false },
            ]
        },
        visibleColumns() {
            return this.allColumns.filter((column) => this.selectedColumnKeys.includes(column.key))
        },
        paginatedItems() {
            return this.items.data || []
        },
        totalPages() {
            return this.items.last_page || 1
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        moment,
        navigateToCreate() {
            window.location.href = '/back-office/v3/promotions/create'
        },
        handlePageChange(page) {
            this.currentPage = page
            this.fetchItems()
        },
        handlePerPageChange(perPage) {
            this.itemsPerPage = Number(perPage)
            setPerPage('promotions_items_per_page', this.itemsPerPage)
            this.fetchItems()
        },
        handleSearchUpdate(searchValue) {
            this.searchQuery = searchValue
            this.currentPage = 1
            this.fetchItems()
        },
        handleStatusChange(value) {
            this.statusFilter = value
            this.currentPage = 1
            this.fetchItems()
        },
        handleSort(key) {
            if (this.orderBy === key) {
                this.orderDirection = this.orderDirection === 'asc' ? 'desc' : 'asc'
            } else {
                this.orderBy = key
                this.orderDirection = 'asc'
            }
            this.currentPage = 1
            this.fetchItems()
        },
        toggleActionMenu(itemId) {
            if (this.activeActionMenu === itemId) {
                this.activeActionMenu = null
            } else {
                this.activeActionMenu = itemId
                this.$nextTick(() => {
                    this.calculateDropdownPosition(itemId)
                })
            }
        },
        calculateDropdownPosition(itemId) {
            const button = this.$refs[`actionButton-${itemId}`]
            const btnEl = Array.isArray(button) ? button[0] : button
            if (!btnEl) return

            const rect = btnEl.getBoundingClientRect()
            this.dropdownPosition = {
                top: `${rect.bottom + 4}px`,
                left: `${rect.right - 220}px`,
            }
        },
        async fetchItems() {
            this.isLoading = true
            this.error = null
            try {
                const params = {
                    search: this.searchQuery,
                    status: this.statusFilter,
                    currentPage: this.currentPage,
                    itemsPerPage: this.itemsPerPage,
                    sortBy: this.orderBy,
                    sortDirection: this.orderDirection,
                }
                const response = await axios.get('/api/v3/promotion/list', {
                    params,
                    headers: { Authorization: `Bearer ${this.token}` },
                })
                const data = response.data
                this.items = {
                    data: data.data || [],
                    total: data.total || 0,
                    last_page: data.last_page || 1,
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                this.error = error.message || this.$t('promotions.fetch_error')
            } finally {
                this.isLoading = false
            }
        },
        async handleToggleStatus(id, isDisabled) {
            this.activeActionMenu = null
            try {
                await axios.patch(
                    `/api/v3/promotion/${id}/toggle-status`,
                    {},
                    { headers: { Authorization: `Bearer ${this.token}` } }
                )
                this.fetchItems()
                this.$toast.success(isDisabled ? this.$t('common.enabled') : this.$t('common.disabled'))
            } catch (error) {
                // 409 : on désactive un code utilisé sur des devis en attente → mêmes options
                // que la suppression (désactiver seulement / révoquer).
                if (error.response && error.response.status === 409) {
                    this.itemToDelete = id
                    this.inUseCount = error.response.data?.count || 0
                    this.showInUseDialog = true
                    return
                }
                this.$toast.error(error.response?.data?.message || this.$t('common.error'))
                console.error('Error toggling status:', error)
            }
        },
        deleteItem(id) {
            this.activeActionMenu = null
            this.itemToDelete = id
            this.showDeletePopup = true
        },
        async confirmDelete() {
            try {
                await axios.delete(`/api/v3/promotion/${this.itemToDelete}`, {
                    headers: { Authorization: `Bearer ${this.token}` },
                })
                this.showDeletePopup = false
                this.itemToDelete = null
                this.fetchItems()
                this.$toast.success(this.$t('common.deleted_successfully'))
            } catch (error) {
                // 409 : code utilisé sur des devis en attente → on ne supprime pas, on propose de
                // le désactiver (garder sur ces devis) ou de le révoquer (l'en retirer).
                if (error.response && error.response.status === 409) {
                    this.showDeletePopup = false
                    this.inUseCount = error.response.data?.count || 0
                    this.showInUseDialog = true
                    return
                }
                this.$toast.error(error.response?.data?.message || this.$t('common.error'))
                console.error('Error deleting promotion:', error)
            }
        },
        // Option « Désactiver » : le code reste sur les devis existants, n'est plus proposé pour
        // les nouveaux. (toggle-status ne bascule que s'il est encore actif.)
        async disableInUse() {
            const item = this.items.find((i) => i.id === this.itemToDelete)
            try {
                if (!item || !item.disabled) {
                    // force : « désactiver seulement » contourne le garde-fou du toggle-status.
                    await axios.patch(
                        `/api/v3/promotion/${this.itemToDelete}/toggle-status`,
                        { force: true },
                        { headers: { Authorization: `Bearer ${this.token}` } }
                    )
                }
                this.closeInUseDialog()
                this.fetchItems()
                this.$toast.success(this.$t('common.disabled'))
            } catch (error) {
                this.$toast.error(error.response?.data?.message || this.$t('common.error'))
            }
        },
        // Option « Révoquer » : désactive le code ET le retire des devis en attente.
        async revokeInUse() {
            try {
                await axios.patch(
                    `/api/v3/promotion/${this.itemToDelete}/revoke`,
                    {},
                    { headers: { Authorization: `Bearer ${this.token}` } }
                )
                this.closeInUseDialog()
                this.fetchItems()
                this.$toast.success(this.$t('common.disabled'))
            } catch (error) {
                this.$toast.error(error.response?.data?.message || this.$t('common.error'))
            }
        },
        closeInUseDialog() {
            this.showInUseDialog = false
            this.itemToDelete = null
            this.inUseCount = 0
        },
        handleClickOutside(event) {
            if (this.activeActionMenu) {
                const actionButton = this.$refs[`actionButton-${this.activeActionMenu}`]
                const actionMenu = this.$refs[`actionMenu-${this.activeActionMenu}`]
                const btnEl = Array.isArray(actionButton) ? actionButton[0] : actionButton
                const menuEl = Array.isArray(actionMenu) ? actionMenu[0] : actionMenu

                if (!btnEl?.contains(event.target) && !menuEl?.contains(event.target)) {
                    this.activeActionMenu = null
                }
            }
        },
        handlePageShow(event) {
            // persisted = true means the page was restored from the bfcache (browser back/forward)
            if (event.persisted) {
                this.activeActionMenu = null
            }
        },
    },
    mounted() {
        eventBus.$on('dt-selected-perpage-changed', this.handlePerPageChange)
        this.fetchItems()
        document.addEventListener('click', this.handleClickOutside)
        window.addEventListener('pageshow', this.handlePageShow)
    },
    activated() {
        // Reset when component is re-activated inside <keep-alive>
        this.activeActionMenu = null
    },
    beforeDestroy() {
        eventBus.$off('dt-selected-perpage-changed', this.handlePerPageChange)
        document.removeEventListener('click', this.handleClickOutside)
        window.removeEventListener('pageshow', this.handlePageShow)
    },
}
</script>
<style scoped lang="scss">
.menuDropdown {
    background: white;
    color: #94a3b8;
    margin-top: 10px;
    z-index: 50;
    padding: 8px 0;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.dropdownItem:hover {
    background-color: #1a1f37;
    color: white !important;
}

.dropdownItem:hover * {
    color: white !important;
}

.actionsDropdown {
    position: fixed;
    background: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-radius: 12px;
    padding: 4px 0;
}

.actionItem {
    background: transparent;
    border: none;
    color: #1a1f37;
    text-decoration: none;
    display: flex;
    align-items: center;
}

.actionItem:hover {
    background-color: #f3f4f6;
    color: #1a1f37 !important;
}

.actionItem:hover * {
    color: #1a1f37 !important;
}

.dark {
    .menuDropdown {
        background-color: #2c2c2ce5;
    }

    .actionsDropdown {
        background-color: #2c2c2ce5;
    }

    .actionItem {
        color: #bfbfbf;
    }

    .actionItem:hover {
        background-color: #1a1f37;
        color: white !important;
    }

    .actionItem:hover * {
        color: white !important;
    }
}

.textGray {
    color: #1a1f37;
}

.dark {
    .textGray {
        color: #bfbfbf;
    }
}
</style>
